import {isEmpty} from 'lodash';
import React, {FC, PropsWithChildren, useEffect, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {TagModel} from '../../../models/api/tagModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getTagsTable} from '../../../store/data/tags/tagsSelectors';
import {LabeledInputDropdown} from '../../common/dropdowns/labeledInputDropdown';
import {BaseFilterDropdownProps} from '../../common/filters/common/types';
import {
  LensFormExcludeTagsModalFilterDropdown,
  renderTagSelectionValue,
  StyledValueWrapper,
} from './lensFormExcludeTagsModalFilterDropdown';
import {
  fetchMissingTags,
  mapIdsToUndeletedTags,
  StyledTagIcon,
  StyledTagTitle,
  StyledTagWrapper,
  TagsMap,
} from './tagFilterHelpers';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

export interface LensFormMultiSelectExcludeTagsProps
  extends Omit<LensFormMultiselectProps<TagModel>, 'choices'> {
  namespace: string | undefined;
  inboxIds: ReadonlyArray<number>;
  noTags: boolean;
  onSetNoTags: (arg: boolean) => void;
  idsToDisable: ReadonlyArray<number>;
}

/*
 * Component.
 */

export const LensFormMultiSelectExcludeTags: FC<PropsWithChildren<LensFormMultiSelectExcludeTagsProps>> = (
  props,
) => {
  const {isDisabled, selectedIds, onSetItemIds, namespace, inboxIds, noTags, onSetNoTags, idsToDisable} =
    props;

  const company = useAppSelector(getCompany);
  const [initialSelectedTags, setInitialSelectedTags] = useState<ReadonlyArray<SearchTagModel>>([]);
  // Tags that are available for using to render the dropdown button value.
  //  These are tags that have been selected at some point and may or may not be currently selected.
  //  We only store these to guarantee we can show the name of selected tags as the button value.
  const [tagsMap, setTagsMap] = useState<TagsMap>({});
  const {byId: tags, deletedById} = useAppSelector(getTagsTable);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  useInitialEffect(() => {
    fetchMissingTags(selectedIds, deletedById, company, tags, dispatch, setTagsMap);
  });

  useEffect(() => {
    setInitialSelectedTags(mapIdsToUndeletedTags(selectedIds, deletedById, tagsMap, tags));
  }, [tags, tagsMap, selectedIds, deletedById]);

  const renderModalDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <LensFormExcludeTagsModalFilterDropdown
      initialSelectedTags={initialSelectedTags}
      tagsMap={tagsMap}
      setTagsMap={setTagsMap}
      company={company}
      namespace={namespace}
      inboxIds={inboxIds}
      noTags={noTags}
      onSetNoTags={onSetNoTags}
      selectedIds={selectedIds}
      onSetItemIds={onSetItemIds}
      anchor={dropdownProps.anchor}
      onRequestClose={dropdownProps.onRequestClose}
      idsToDisable={idsToDisable}
    />
  );

  return (
    <LabeledInputDropdown
      renderDropdown={renderModalDropdown}
      renderValue={() => renderValue(noTags, selectedIds, tagsMap, theme)}
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-exclude-tags-placeholder">Select shared tags</Trans>
      )}
      label={
        props.label || (
          <Trans id="lens-form-multiselect-exclude-tags-label">Excluding all of these tags</Trans>
        )
      }
      isDisabled={isDisabled}
      isRequired={false}
      messageForDisabled={
        <Trans id="lens-form-multiselect-missing-inbox-message">
          Field will be enabled once an inbox is selected.
        </Trans>
      }
      hasError={Boolean(props.hasError)}
      errorMessage={props.errorMessage}
      hasPreviousSelection={!isEmpty(selectedIds) || noTags}
    />
  );
};

/*
 * Helpers.
 */

function renderValue(
  noTags: boolean,
  selectedIds: ReadonlyArray<number>,
  tagsMap: TagsMap,
  theme: UserTheme,
) {
  if (noTags) {
    return (
      <StyledValueWrapper>
        <StyledTagWrapper key="lens-form-no-tags-filter-value-wrapper">
          <StyledTagIcon>{<Icon name="noTagSmall" color={theme.greys.shade60} />}</StyledTagIcon>
          <StyledTagTitle>
            <Trans id="lens-form-no-tags-filter-value">Doesn&apos;t have any tags</Trans>
          </StyledTagTitle>
        </StyledTagWrapper>
      </StyledValueWrapper>
    );
  }

  return renderTagSelectionValue(selectedIds, tagsMap, theme);
}
